import React from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import styles from "../../../styles/styles";
import { backend_url } from "../../../server";

const buildImageUrl = (image) => {
  if (!image) return "";

  if (image.startsWith("http://") || image.startsWith("https://") || image.startsWith("data:")) {
    return image;
  }

  const normalized = image.startsWith("/") ? image.slice(1) : image;
  return `${backend_url}${normalized}`;
};

const normalizeLink = (link, fallback) => {
  if (!link || typeof link !== "string") return fallback;
  const trimmed = link.trim();
  if (trimmed === "") return fallback;

  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("mailto:") ||
    trimmed.startsWith("tel:")
  ) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  if (trimmed.includes("?")) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  return fallback;
};

const handleLink = (navigate, link) => {
  if (!link) return;

  if (link.startsWith("http://") || link.startsWith("https://")) {
    window.open(link, "_blank", "noopener,noreferrer");
    return;
  }

  navigate(link);
};

const normalizeCatalogCategories = (catalogCategories = [], selectedIds = []) => {
  const selectionSet = new Set(
    (selectedIds || [])
      .map((id) => (id ? id.toString() : ""))
      .filter((id) => id !== "")
  );

  const normalized = catalogCategories
    .filter((item) => item && item.name && item.isActive !== false)
    .sort(
      (a, b) => (typeof a.order === "number" ? a.order : 0) - (typeof b.order === "number" ? b.order : 0)
    )
    .map((item) => {
      const title = item.name?.trim() || "";
      const slugSource = item.slug?.trim() || title;
      const fallbackLink = slugSource
        ? `/products?category=${encodeURIComponent(slugSource)}`
        : "/products";

      return {
        id: item._id?.toString() || "",
        title,
        link: normalizeLink(item.link, fallbackLink),
        image: item.image || item.icon || "",
        slug: item.slug,
      };
    });

  if (selectionSet.size === 0) {
    return [];
  }

  return normalized.filter((item) => item.id && selectionSet.has(item.id));
};

const BrandingItemCard = ({ item }) => (
  <div className="flex items-start gap-3 w-full">
    {item.image ? (
      <img
        src={buildImageUrl(item.image)}
        alt={item.title}
        className="w-10 h-10 object-contain shrink-0"
      />
    ) : null}
    <div className="px-1 min-w-0">
      <h3 className="font-bold text-sm md:text-base">{item.title}</h3>
      {item.description && (
        <p className="text-xs md:text-sm text-gray-600 mt-0.5">{item.description}</p>
      )}
    </div>
  </div>
);

const Categories = ({ branding = {}, categories = {}, catalogCategories = [] }) => {
    const navigate = useNavigate();
  const brandingItems = branding.items || [];
  const selectedCatalogCategoryIds = categories.catalogCategoryIds || [];
  const catalogCategoryItems = normalizeCatalogCategories(
    catalogCategories,
    selectedCatalogCategoryIds
  );
  const categoryItems = catalogCategoryItems;
  const visibleCategoryItems = categoryItems.slice(0, 5);

    return (
        <>
      <div className={`${styles.section}`}>
        {brandingItems.length > 0 && (
          <div className="branding my-12 w-full shadow-sm bg-white p-5 rounded-md">
            <div className="sm:hidden branding-carousel">
              <Swiper
                modules={[Autoplay]}
                slidesPerView={1}
                spaceBetween={16}
                loop={brandingItems.length > 1}
                autoplay={
                  brandingItems.length > 1
                    ? { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }
                    : false
                }
                className="w-full"
              >
                {brandingItems.map((item) => (
                  <SwiperSlide key={item._id || item.title}>
                    <BrandingItemCard item={item} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="hidden sm:flex gap-4 justify-between flex-wrap">
              {brandingItems.map((item) => (
                <div className="flex-1 min-w-[180px]" key={item._id || item.title}>
                  <BrandingItemCard item={item} />
                </div>
              ))}
            </div>
          </div>
        )}
            </div>

      {visibleCategoryItems.length > 0 && (
        <div className={`${styles.section} py-6 rounded-lg mb-12`} id="categories">
          {categories.heading && (
            <h2 className="text-xl font-semibold mb-5">{categories.heading}</h2>
          )}
          {categories.subheading && (
            <p className="text-sm text-gray-500 mt-1 mb-5">{categories.subheading}</p>
          )}
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visibleCategoryItems.map((item) => {
              const title = item.title || item.name;
              const imageSource = item.image || item.image_Url || item.icon || "";
              const fallbackLink = item.link;

              return (
                <div
                  key={item.id || item.slug || title}
                  role="button"
                  tabIndex={0}
                  className="group flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:border-[#38513b] hover:shadow-md transition overflow-hidden cursor-pointer"
                  onClick={() => handleLink(navigate, fallbackLink)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleLink(navigate, fallbackLink);
                  }}
                >
                  <div className="aspect-square w-full rounded-lg overflow-hidden mb-3">
                    {imageSource ? (
                      <img
                        src={buildImageUrl(imageSource)}
                        alt={title}
                        className="h-full w-full object-contain group-hover:scale-105 transition"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-400 text-4xl font-light">
                        {title ? title.charAt(0) : "?"}
                      </div>
                    )}
                  </div>
                  <h2 className="text-base font-semibold text-slate-900 group-hover:text-[#38513b] text-center">
                    {title || item.title}
                  </h2>
                </div>
              );
            })}
          </div>
        </div>
      )}
        </>
  );
};

export default Categories;